/**
 * First-run onboarding wizard (chromeless — no app sidebar/tab bar). Four steps:
 * Welkom · Taal · Oefenen · Klaar. The exam-date step is cut for v1 (no backend).
 *
 * Two real persistence side effects:
 *   - Language persists the moment the user picks in Step 2, via the reused
 *     LanguageSwitcher (initI18n + cookie + POST /user/preferences). Picking it
 *     re-renders the rest of the wizard chrome in the chosen language.
 *   - Module persists only on finish, via setDefaultModule — which also "completes"
 *     onboarding (welkom.tsx redirects returning users whose defaultModule is set).
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import { MODULE_DEFINITIONS, type ModuleSlug } from "@a2/core";
import type { SessionUser } from "../../../lib/session-loader";
import { currentLocale } from "../../../lib/i18n";
import { setDefaultModule } from "../../../lib/use-default-module";
import { useT } from "../../../lib/use-t";
import { Button } from "../../ui/Button";
import { LanguageSwitcher } from "../../LanguageSwitcher";
import { OnboardingBrandPane, OnboardingProgress } from "./OnboardingChrome";
import { StepOefenen } from "./StepOefenen";
import { StepKlaar } from "./StepKlaar";

const TOTAL = 4;

function HomeIcon() {
  return (
    <span className="mb-[22px] grid h-[60px] w-[60px] place-items-center rounded-[18px] bg-[var(--warm-soft)] text-[var(--warm)]">
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 11l9-8 9 8M5 9.5V20h14V9.5" />
        <path d="M9 21v-6h6v6" />
      </svg>
    </span>
  );
}

export function OnboardingWizard({ session }: { session: SessionUser }) {
  const t = useT();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [module, setModule] = useState<ModuleSlug>("spreken");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  // Recap language reads the live locale — react-i18next re-renders this
  // component when the Step 2 switcher changes it, so no separate draft state.
  const lang = currentLocale();

  const labels = [
    t("onboarding.rail.welkom"),
    t("onboarding.rail.taal"),
    t("onboarding.rail.oefenen"),
    t("onboarding.rail.klaar"),
  ];

  const back = () => setStep((s) => Math.max(0, s - 1));
  const next = () => setStep((s) => Math.min(TOTAL - 1, s + 1));
  const jump = (i: number) => i < step && setStep(i);

  async function finish() {
    setError(false);
    setSubmitting(true);
    try {
      await setDefaultModule(module);
      nav(MODULE_DEFINITIONS[module].route, { replace: true });
    } catch {
      setError(true);
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg)] lg:flex-row">
      <OnboardingBrandPane step={step} labels={labels} quote={t("onboarding.brandQuote")} onJump={jump} />
      <OnboardingProgress step={step} total={TOTAL} />

      <div className="relative flex flex-1 flex-col px-5 py-7 lg:px-11 lg:py-10">
        <div
          key={step}
          className="animate-tip mx-auto flex w-full max-w-[480px] flex-1 flex-col justify-center pb-24 lg:pb-0"
        >
          {step === 0 ? (
            <div>
              <HomeIcon />
              <p className="font-mono-badge text-[11px] uppercase tracking-[0.14em] text-[var(--ink-faint)]">
                {t("onboarding.welkom.eyebrow")}
              </p>
              <h1 className="mt-3 text-[26px] font-semibold tracking-tight lg:text-[32px]">
                {t("onboarding.welkom.title")}
              </h1>
              <p className="mb-7 mt-3.5 font-serif-italic text-[19px] leading-[1.45] text-[var(--ink-muted)]">
                {t("onboarding.welkom.lead")}
              </p>
            </div>
          ) : null}

          {step === 1 ? (
            <div>
              <p className="font-mono-badge text-[11px] uppercase tracking-[0.14em] text-[var(--ink-faint)]">
                {t("onboarding.taal.eyebrowFmt", { n: 2 })}
              </p>
              <h1 className="mt-3 text-[26px] font-semibold tracking-tight lg:text-[32px]">
                {t("onboarding.taal.title")}
              </h1>
              <p className="mb-7 mt-3.5 font-serif-italic text-[19px] leading-[1.45] text-[var(--ink-muted)]">
                {t("onboarding.taal.lead")}
              </p>
              <LanguageSwitcher variant="sidebar" />
              <p className="mt-4 text-[12.5px] text-[var(--ink-faint)]">{t("onboarding.taal.helper")}</p>
            </div>
          ) : null}

          {step === 2 ? (
            <div>
              <p className="font-mono-badge text-[11px] uppercase tracking-[0.14em] text-[var(--ink-faint)]">
                {t("onboarding.oefenen.eyebrowFmt", { n: 3 })}
              </p>
              <h1 className="mt-3 text-[26px] font-semibold tracking-tight lg:text-[32px]">
                {t("onboarding.oefenen.title")}
              </h1>
              <p className="mb-7 mt-3.5 font-serif-italic text-[19px] leading-[1.45] text-[var(--ink-muted)]">
                {t("onboarding.oefenen.lead")}
              </p>
              <StepOefenen statusMap={session.moduleStatusMap} value={module} onChange={setModule} />
            </div>
          ) : null}

          {step === 3 ? <StepKlaar lang={lang} module={module} /> : null}
        </div>

        {/* Navbar — sticky to the bottom on mobile, in-flow on desktop. */}
        <div className="absolute inset-x-0 bottom-0 z-10 border-t border-[var(--border)] bg-[var(--panel)] px-5 py-3.5 lg:static lg:mx-auto lg:w-full lg:max-w-[480px] lg:border-0 lg:bg-transparent lg:px-0 lg:py-0">
          {step === 3 ? (
            <div className="mx-auto flex w-full max-w-[480px] items-center gap-3 lg:max-w-none lg:border-t lg:border-[var(--border)] lg:pt-5">
              <Button variant="ghost" size="lg" onClick={back} disabled={submitting}>
                ← {t("onboarding.nav.back")}
              </Button>
              <div className="flex-1" />
              <Button variant="secondary" size="lg" onClick={() => nav("/vandaag")} disabled={submitting}>
                {t("onboarding.klaar.toVandaag")}
              </Button>
              <Button variant="warm" size="lg" onClick={finish} disabled={submitting}>
                {t("onboarding.klaar.startPractice")}
              </Button>
            </div>
          ) : (
            <div className="mx-auto flex w-full max-w-[480px] items-center gap-3 lg:max-w-none lg:border-t lg:border-[var(--border)] lg:pt-5">
              {step > 0 ? (
                <Button variant="ghost" size="lg" onClick={back}>
                  ← {t("onboarding.nav.back")}
                </Button>
              ) : null}
              <div className="flex-1" />
              {step === 0 ? (
                <Button variant="warm" size="lg" onClick={next}>
                  {t("onboarding.welkom.cta")}
                </Button>
              ) : (
                <Button variant="primary" size="lg" onClick={next}>
                  {t("onboarding.nav.next")} →
                </Button>
              )}
            </div>
          )}
          {error ? (
            <p role="alert" className="mx-auto mt-2 w-full max-w-[480px] text-sm text-[var(--fail)]">
              {t("onboarding.klaar.saveError")}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
